import { useState, type ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Skeleton from "@mui/material/Skeleton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { flagEnabled } from "../../config";
import { ApiError } from "../../api/errors";
import { greetingFor, formatCount } from "../../api/welcome";
import { listTenants, listInvites, KIND_LABEL, STATE_LABEL, SMS_LABEL, type PlatformTenant, type PlatformInvite } from "../../api/platform";
import { listAnkhCustomers, customerState, type AnkhCustomer } from "../../api/ankhCustomers";
import { fetchReport, REPORTS } from "../../api/reports";
import { ANKH_CUSTOMERS_PATH, canReadAnkhCustomers, canReadPlatformTenants, canReadSmsLog, PLATFORM_PATH } from "../../components/nav";
import { isoDay } from "../../components/FilterBar";

/**
 * The io.ankhos Welcome Page (owner 2026-10-24: "for io.ankhos, please
 * redesign dashboard view also. It is old operator dashboard view").
 *
 * io.ankhos is the platform, not an operator: it has no plans, sessions or
 * hotspot traffic of its own, so the operator cards answered nothing useful
 * here. This page answers what the platform team acts on: tenants, who needs
 * attention, open invitations, which customers can be invoiced, and SMS.
 *
 * 🔑 Each card is gated exactly as the menu gates its screen (nav.ts):
 * tenants and invitations need canReadPlatformTenants, Ankh customers need
 * canReadAnkhCustomers (onboarding staff do not see invoices), the SMS card is
 * for everyone who reaches io.ankhos. A card the login cannot read is absent,
 * not "unavailable".
 */

const DATE = new Intl.DateTimeFormat("en-IN", { weekday: "long", day: "numeric", month: "long" });

/** Tenant states someone at Ankh has to act on. */
export const NEEDS_ATTENTION = ["APPLIED", "IN_REVIEW", "OVERDUE", "RESTRICTED"] as const;
/** Invitations still waiting for the invitee. */
export const OPEN_INVITES = ["SENT", "OPENED"] as const;

export interface PlatformCounts {
  live: number;
  test: number;
  trial: number;
  attention: number;
}

/** The tenant numbers the cards show, from one tenant list. */
export function platformCounts(tenants: PlatformTenant[]): PlatformCounts {
  return {
    live: tenants.filter((t) => t.environment === "live").length,
    test: tenants.filter((t) => t.environment === "test").length,
    trial: tenants.filter((t) => t.state === "TRIAL").length,
    attention: tenants.filter((t) => (NEEDS_ATTENTION as readonly string[]).includes(t.state)).length,
  };
}

export function openInvites(invites: PlatformInvite[]): PlatformInvite[] {
  return invites.filter((i) => (OPEN_INVITES as readonly string[]).includes(i.status));
}

/** Customers Ankh can invoice now, and the ones still missing something. */
export function customerSplit(customers: AnkhCustomer[]): { ready: AnkhCustomer[]; pending: AnkhCustomer[] } {
  const ready = customers.filter((c) => customerState(c) === "ready");
  return { ready, pending: customers.filter((c) => customerState(c) !== "ready" && customerState(c) !== "test") };
}

function reason(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.kind === "forbidden") return "Refused: your role cannot read this.";
    if (e.kind === "not-found") return "Not available on this gateway yet.";
    return e.message;
  }
  return e instanceof Error ? e.message : String(e);
}

function Card({
  label,
  hint,
  to,
  value,
  state,
}: {
  label: string;
  hint: string;
  to: string;
  value: number | undefined;
  state: { isLoading: boolean; error: unknown };
}) {
  const body = state.isLoading ? (
    <Skeleton variant="text" width={72} sx={{ fontSize: 34 }} />
  ) : state.error ? (
    <Tooltip title={reason(state.error)}>
      <Typography component="span" variant="body2" color="text.secondary" sx={{ lineHeight: "34px" }}>
        unavailable
      </Typography>
    </Tooltip>
  ) : (
    <Typography component="span" variant="h4" sx={{ lineHeight: 1, letterSpacing: "-0.02em" }} data-testid={`pstat-${label}`}>
      {formatCount(value ?? 0)}
    </Typography>
  );
  return (
    <Box
      component={RouterLink}
      to={to}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
        p: 2,
        border: 1,
        borderColor: "divider",
        borderRadius: "8px",
        textDecoration: "none",
        color: "inherit",
        "&:hover": { borderColor: "primary.main" },
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      {body}
      <Typography variant="caption" color="text.secondary">
        {hint}
      </Typography>
    </Box>
  );
}

function Section({ title, aside, action, children }: { title: string; aside?: string | undefined; action?: ReactNode; children: ReactNode }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5 }}>
        <Typography variant="h6" component="h2" sx={{ fontSize: 18 }}>
          {title}
        </Typography>
        {aside && (
          <Typography variant="caption" color="text.disabled">
            {aside}
          </Typography>
        )}
        <Box sx={{ flexGrow: 1 }} />
        {action}
      </Box>
      {children}
    </Box>
  );
}

const cell = { py: 0.75, fontSize: 13 } as const;

export function PlatformWelcome() {
  const identity = useIdentity();
  const [now] = useState(() => new Date());
  const ready = Boolean(identity.token && identity.namespace);
  const tenantsOK = canReadPlatformTenants(identity.roles) && flagEnabled("platform.tenants");
  const customersOK = canReadAnkhCustomers(identity.roles) && flagEnabled("billing.ankhCustomers");
  const smsOK = canReadSmsLog(identity.roles) && flagEnabled("reports.smsLog");
  const monthStart = isoDay(new Date(now.getFullYear(), now.getMonth(), 1));

  const tenants = useQuery({
    queryKey: ["platform", "welcome", "tenants", identity.namespace],
    enabled: ready && tenantsOK,
    queryFn: ({ signal }) => listTenants(identity, "", signal),
  });
  const invites = useQuery({
    queryKey: ["platform", "welcome", "invites", identity.namespace],
    enabled: ready && tenantsOK,
    queryFn: ({ signal }) => listInvites(identity, signal),
  });
  const customers = useQuery({
    queryKey: ["platform", "welcome", "customers", identity.namespace],
    enabled: ready && customersOK,
    queryFn: ({ signal }) => listAnkhCustomers(identity, signal),
  });
  const sms = useQuery({
    queryKey: ["platform", "welcome", "sms", identity.namespace, monthStart],
    enabled: ready && smsOK,
    queryFn: ({ signal }) =>
      fetchReport(identity, REPORTS.smsLog, { start: `${monthStart}T00:00:00Z`, end: `${isoDay(now)}T23:59:59Z` }, signal),
  });

  const counts = tenants.data ? platformCounts(tenants.data.tenants) : undefined;
  const open = invites.data ? openInvites(invites.data.invites) : [];
  const split = customers.data ? customerSplit(customers.data.customers) : { ready: [], pending: [] };
  const smsRows = Array.isArray(sms.data?.data) ? sms.data.data.length : sms.data ? 0 : undefined;
  const attention = (tenants.data?.tenants ?? []).filter((t) => (NEEDS_ATTENTION as readonly string[]).includes(t.state));
  const recent = [...(tenants.data?.tenants ?? [])].sort((a, b) => b.createdat.localeCompare(a.createdat)).slice(0, 8);
  const st = (q: { isLoading: boolean; error: unknown }) => ({ isLoading: q.isLoading, error: q.error });

  const cards = [
    tenantsOK && <Card key="live" label="Live tenants" hint="paying platform customers" to={PLATFORM_PATH} value={counts?.live} state={st(tenants)} />,
    tenantsOK && <Card key="trial" label="On trial" hint="trial period running" to={PLATFORM_PATH} value={counts?.trial} state={st(tenants)} />,
    tenantsOK && (
      <Card key="attention" label="Needs attention" hint="applied, in review, overdue or restricted" to={PLATFORM_PATH} value={counts?.attention} state={st(tenants)} />
    ),
    tenantsOK && <Card key="invites" label="Open invitations" hint="sent, not yet accepted" to={PLATFORM_PATH} value={invites.data ? open.length : undefined} state={st(invites)} />,
    customersOK && (
      <Card key="ready" label="Ready to invoice" hint="open account, live invoices" to={ANKH_CUSTOMERS_PATH} value={customers.data ? split.ready.length : undefined} state={st(customers)} />
    ),
    smsOK && <Card key="sms" label="SMS this month" hint="sent or logged from io.ankhos" to="/reports/sms-log" value={smsRows} state={st(sms)} />,
  ].filter(Boolean);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }} data-testid="platform-welcome">
      <Box sx={{ display: "flex", alignItems: "flex-end", gap: 2, flexWrap: "wrap" }}>
        <Box>
          <Typography variant="h5" component="h1" sx={{ letterSpacing: "-0.01em" }}>
            {greetingFor(now.getHours())}
            {identity.firstName ? `, ${identity.firstName}` : ""}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {DATE.format(now)} · Ankh platform · <code>{identity.namespace}</code>
          </Typography>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        {tenantsOK && (
          <Button component={RouterLink} to={PLATFORM_PATH} variant="contained" color="secondary">
            Tenants &amp; invitations
          </Button>
        )}
      </Box>

      {cards.length > 0 && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))", lg: "repeat(6, minmax(0, 1fr))" }, gap: 2 }}>
          {cards}
        </Box>
      )}

      {tenantsOK && attention.length > 0 && (
        <Section title="Needs attention" aside={`${attention.length} tenant${attention.length === 1 ? "" : "s"}`}>
          <TenantTable rows={attention} />
        </Section>
      )}

      {tenantsOK && (
        <Section
          title="Tenants"
          aside={counts ? `${counts.live} live · ${counts.test} test` : undefined}
          action={
            <Button component={RouterLink} to={PLATFORM_PATH} size="small" sx={{ color: "brand.link" }}>
              All tenants
            </Button>
          }
        >
          {tenants.isLoading ? <Skeleton variant="rounded" height={120} /> : tenants.error ? <Typography variant="body2" color="text.secondary">{reason(tenants.error)}</Typography> : <TenantTable rows={recent} />}
        </Section>
      )}

      {tenantsOK && open.length > 0 && (
        <Section title="Open invitations" aside={`${open.length} waiting`}>
          <TableContainer sx={{ border: 1, borderColor: "divider", borderRadius: "8px" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={cell}>Name</TableCell>
                  <TableCell sx={cell}>Kind</TableCell>
                  <TableCell sx={cell}>Status</TableCell>
                  <TableCell sx={cell}>SMS</TableCell>
                  <TableCell sx={cell}>Expires</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {open.slice(0, 8).map((i) => (
                  <TableRow key={i.id}>
                    <TableCell sx={cell}>{i.name}</TableCell>
                    <TableCell sx={cell}>{KIND_LABEL[i.kind] ?? i.kind}</TableCell>
                    <TableCell sx={cell}>{i.status === "OPENED" ? "Opened" : "Sent"}</TableCell>
                    <TableCell sx={cell}>{i.smsstatus ? (SMS_LABEL[i.smsstatus] ?? i.smsstatus) : "—"}</TableCell>
                    <TableCell sx={cell}>{i.expiresat.slice(0, 10)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Section>
      )}

      {customersOK && split.pending.length > 0 && (
        <Section
          title="Customers to complete before invoicing"
          aside={`${split.pending.length}`}
          action={
            <Button component={RouterLink} to={ANKH_CUSTOMERS_PATH} size="small" sx={{ color: "brand.link" }}>
              Client accounts
            </Button>
          }
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            {split.pending.slice(0, 8).map((c) => (
              <Box key={c.namespace || c.accountid} sx={{ display: "flex", gap: 1, alignItems: "baseline", flexWrap: "wrap" }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {c.legalname || c.namespace}
                </Typography>
                <Chip size="small" variant="outlined" label={customerState(c)} />
                {c.missing.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    missing: {c.missing.join(", ")}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        </Section>
      )}
    </Box>
  );
}

function TenantTable({ rows }: { rows: PlatformTenant[] }) {
  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No tenants yet.
      </Typography>
    );
  }
  return (
    <TableContainer sx={{ border: 1, borderColor: "divider", borderRadius: "8px" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={cell}>Tenant</TableCell>
            <TableCell sx={cell}>Kind</TableCell>
            <TableCell sx={cell}>Environment</TableCell>
            <TableCell sx={cell}>State</TableCell>
            <TableCell sx={cell}>Trial ends</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((t) => (
            <TableRow key={t.accountid}>
              <TableCell sx={cell}>
                <Box sx={{ fontWeight: 600 }}>{t.legalname}</Box>
                {t.namespace && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                    {t.namespace}
                  </Typography>
                )}
              </TableCell>
              <TableCell sx={cell}>{KIND_LABEL[t.kind] ?? t.kind}</TableCell>
              <TableCell sx={cell}>
                <Chip size="small" variant="outlined" label={t.environment} color={t.environment === "live" ? "success" : "default"} />
              </TableCell>
              <TableCell sx={cell}>{STATE_LABEL[t.state] ?? t.state}</TableCell>
              <TableCell sx={cell}>{t.trialendsat ? t.trialendsat.slice(0, 10) : "—"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
